import { LitElement, html, PropertyValues, unsafeCSS } from 'lit';
import { property, query, state, queryAll, customElement } from 'lit/decorators.js';
import { Status, WizardEvent, WizardItem, WizardSelectionEvent, WizardStepStatusChangeEvent } from '../types';
import { styles } from '../view/styles';
import { styles as componentStyles } from '../view/wizard.scss';
import { GzWizardStepWebComponent } from './GzWizardStepWebComponent';

@customElement('gz-wizard')
export class GzWizardWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/properties
  @property({ type: Array, attribute: true, reflect: true })
  options: Array<WizardItem> = [];

  /**
   * The index of the currently active step
   */
  @state()
  activeIndex = 0;

  @query('.wizard')
  private wizard: HTMLElement;

  @queryAll('gz-wizard-step')
  private wizardSteps: NodeListOf<GzWizardStepWebComponent>;

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (changedProperties.has('activeIndex') && changedProperties.get('activeIndex') !== undefined) {
      const previousIndex = (changedProperties.get('activeIndex') as number) || 0;
      this.emitWizardSelectionChangeEvent(previousIndex, this.activeIndex);
    }
  }

  static get styles() {
    return [...styles, unsafeCSS(`${componentStyles}`)];
  }

  render() {
    return html`
      <div class="wizard">
        ${this.options.map((option: WizardItem, index: number) => this.renderWizardItem(option, index))}
      </div>
    `;
  }

  private renderWizardItem(item: WizardItem, index: number) {
    return html`
      <gz-wizard-step
        data-step="${item.step}"
        data-index="data-${index}"
        .step="${item.step}"
        .title="${item.title}"
        .message="${item.message}"
        style="width: ${this.columnWidth()}%"
        ?isVisited="${index <= this.activeIndex}"
        ?isActive="${this.activeIndex === index}"
        ?isOptional="${item.isOptional}"
        @click="${this.handleStepClick}"
      ></gz-wizard-step>
    `;
  }

  //set dynamic width for better ux experience
  public columnWidth() {
    return 100 / this.options.length;
  }

  /**
   * Resets the wizard to its initial state
   */
  public reset() {
    this.activeIndex = 0;
    for (let i = 0; i < this.wizardSteps.length; i++) {
      this.wizardSteps[i].isActive = false;
      this.resetStep(i);
    }
  }

  public updateStep(e) {
    const value = e?.target.getAttribute('data-step') || 1;
    const stepIndex = parseFloat(value) - 1;
    this.getStepFromIndex(stepIndex).isActive = true;
    this.activeIndex = stepIndex;
  }

  // set success status to step
  public completeStep(step: number) {
    this.getStepFromIndex(step).status = Status.COMPLETE;
    this.emitStepStatusChangeEvent(step, Status.COMPLETE);

    // Enable navigation for current step for and next, if there is one
    this.enableStepNavigation(step);

    // If there is a next step, enable it
    if (step === this.options.length - 1) {
      return;
    }
    this.enableStepNavigation(step + 1);
  }

  public enableStepNavigation(step: number) {
    if (this.getStepFromIndex(step).isEnabled) {
      return;
    }
    this.getStepFromIndex(step).isEnabled = true;
  }

  // set fail status to step
  public incompleteStep(step: number) {
    this.getStepFromIndex(step).status = Status.INCOMPLETE;
    this.emitStepStatusChangeEvent(step, Status.INCOMPLETE);
  }

  // reset fail/success status
  public resetStep(step: number) {
    this.getStepFromIndex(step).status = Status.DEFAULT;
    this.emitStepStatusChangeEvent(step, Status.DEFAULT);
  }

  public getStepFromIndex(step: number): GzWizardStepWebComponent {
    return Array.from(this.wizardSteps).filter((elem, index) => index === step)[0];
  }

  private handleStepClick(e: MouseEvent) {
    this.updateStep(e);
  }

  /**
   * Selects the next step in list
   */
  public next() {
    if (this.activeIndex === this.options.length - 1) {
      return;
    }
    this.activeIndex = Math.min(this.activeIndex + 1, this.options.length - 1);
  }

  /**
   * Selects the previous step in list
   */
  public previous() {
    if (this.activeIndex === 0) {
      return;
    }

    this.activeIndex = Math.max(this.activeIndex - 1, 0);
  }

  private emitWizardSelectionChangeEvent(prevIndex: number, currentIndex: number) {
    const event = new WizardSelectionEvent(WizardEvent.SELECTION_CHANGE, {
      detail: {
        selectedIndex: currentIndex,
        previouslySelectedIndex: prevIndex,
        selectedStep: this.getStepFromIndex(currentIndex),
        previouslySelectedStep: this.getStepFromIndex(currentIndex),
      },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(event);
  }

  private emitStepStatusChangeEvent(currentIndex: number, status: Status) {
    const event = new WizardStepStatusChangeEvent(WizardEvent.STEP_STATUS_CHANGE, {
      detail: {
        stepIndex: currentIndex,
        stepStatus: status,
      },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(event);
  }
}
